/* ============================================================
   BROTROX hero-3d.js
   Progressive enhancement: a floating 3D supplement tub with
   volt/bolt rim lighting and an energy particle field.
   - Loads three.js (r128) from CDN only when the hero container
     exists, after window load, so it never blocks LCP.
   - Skips entirely on prefers-reduced-motion or WebGL failure.
   - Pauses when the hero scrolls out of view (battery friendly).
   ============================================================ */
(function () {
  'use strict';

  var host = document.querySelector('[data-hero-3d]');
  if (!host) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  function boot() {
    var s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
    s.async = true;
    s.onload = function () { try { init(); } catch (e) { /* fail silently */ } };
    document.head.appendChild(s);
  }
  // Start after full load (loader is fading out around then)
  if (document.readyState === 'complete') boot();
  else window.addEventListener('load', boot);

  function cssVar(name, fallback) {
    var v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
  }

  function init() {
    var VOLT = new THREE.Color(cssVar('--c-volt', '#A9E520'));
    var BOLT = new THREE.Color(cssVar('--c-bolt', '#1E8FFF'));

    var renderer;
    try {
      renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    } catch (e) { return; }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(host.clientWidth, host.clientHeight);
    host.appendChild(renderer.domElement);

    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(36, host.clientWidth / host.clientHeight, 0.1, 100);
    camera.position.set(0, 0.5, 8);

    /* ── Lights: soft base + the two brand rims ── */
    scene.add(new THREE.AmbientLight(0xffffff, 0.25));
    var key = new THREE.DirectionalLight(0xffffff, 0.5);
    key.position.set(2, 4, 5);
    scene.add(key);
    var voltLight = new THREE.PointLight(VOLT.getHex(), 1.4, 20);
    voltLight.position.set(-4, 2, 3);
    scene.add(voltLight);
    var boltLight = new THREE.PointLight(BOLT.getHex(), 1.4, 20);
    boltLight.position.set(4, -1.5, 3);
    scene.add(boltLight);

    /* ── The tub ── */
    var tub = new THREE.Group();

    var bodyMat = new THREE.MeshStandardMaterial({ color: 0x101318, metalness: 0.55, roughness: 0.32 });
    var body = new THREE.Mesh(new THREE.CylinderGeometry(1.28, 1.22, 2.5, 48), bodyMat);
    tub.add(body);

    var lidMat = new THREE.MeshStandardMaterial({ color: 0x0B0D10, metalness: 0.7, roughness: 0.25 });
    var lid = new THREE.Mesh(new THREE.CylinderGeometry(1.36, 1.36, 0.46, 48), lidMat);
    lid.position.y = 1.48;
    tub.add(lid);

    var lidTop = new THREE.Mesh(new THREE.CylinderGeometry(1.2, 1.32, 0.14, 48), lidMat);
    lidTop.position.y = 1.78;
    tub.add(lidTop);

    // Label band (slightly lighter so the rim lights catch it)
    var band = new THREE.Mesh(
      new THREE.CylinderGeometry(1.29, 1.24, 1.6, 48, 1, true),
      new THREE.MeshStandardMaterial({ color: 0x181C22, metalness: 0.3, roughness: 0.5, side: THREE.DoubleSide })
    );
    band.position.y = -0.15;
    tub.add(band);

    /* ── Brand label: the logo wrapped onto the front of the tub ──
       A partial cylinder segment (about 130°) sits a hair outside the
       band and carries the logo texture. MeshBasicMaterial keeps the
       logo crisp and evenly bright regardless of the scene lights;
       the logo's own black background blends into the dark band. */
    var logoUrl = host.dataset.logo;
    if (logoUrl) {
      new THREE.TextureLoader().load(logoUrl, function (tex) {
        tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
        // Size the arc from the logo's aspect ratio so it never stretches
        var h = 1.15, r = 1.305;
        var aspect = (tex.image && tex.image.height) ? tex.image.width / tex.image.height : 1.7;
        var arc = Math.min(2.6, (aspect * h) / r);
        var label = new THREE.Mesh(
          new THREE.CylinderGeometry(r, r, h, 48, 1, true, -arc / 2, arc),
          new THREE.MeshBasicMaterial({ map: tex, side: THREE.FrontSide })
        );
        label.position.y = -0.15;
        tub.add(label);
      });
    }

    // The energy streak: one volt ring, one bolt ring (emissive)
    var voltRing = new THREE.Mesh(
      new THREE.TorusGeometry(1.3, 0.028, 12, 64),
      new THREE.MeshStandardMaterial({ color: VOLT, emissive: VOLT, emissiveIntensity: 0.9, metalness: 0.2, roughness: 0.3 })
    );
    voltRing.rotation.x = Math.PI / 2;
    voltRing.position.y = 0.72;
    tub.add(voltRing);

    var boltRing = new THREE.Mesh(
      new THREE.TorusGeometry(1.27, 0.028, 12, 64),
      new THREE.MeshStandardMaterial({ color: BOLT, emissive: BOLT, emissiveIntensity: 0.9, metalness: 0.2, roughness: 0.3 })
    );
    boltRing.rotation.x = Math.PI / 2;
    boltRing.position.y = -1.02;
    tub.add(boltRing);

    tub.rotation.z = -0.06;
    scene.add(tub);

    /* ── Energy particles: half volt, half bolt ── */
    function particleSystem(color, count, spread) {
      var geo = new THREE.BufferGeometry();
      var pos = new Float32Array(count * 3);
      for (var i = 0; i < count * 3; i += 3) {
        pos[i]     = (Math.random() - 0.5) * spread;
        pos[i + 1] = (Math.random() - 0.5) * spread * 0.8;
        pos[i + 2] = (Math.random() - 0.5) * spread * 0.6 - 1;
      }
      geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      var mat = new THREE.PointsMaterial({
        color: color, size: 0.035, transparent: true, opacity: 0.75,
        blending: THREE.AdditiveBlending, depthWrite: false
      });
      return new THREE.Points(geo, mat);
    }
    var voltDust = particleSystem(VOLT, 90, 10);
    var boltDust = particleSystem(BOLT, 90, 10);
    scene.add(voltDust, boltDust);

    /* ── Interaction & animation ── */
    var mouseX = 0, mouseY = 0;
    window.addEventListener('pointermove', function (e) {
      mouseX = (e.clientX / window.innerWidth) - 0.5;
      mouseY = (e.clientY / window.innerHeight) - 0.5;
    }, { passive: true });

    var running = true;
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        running = entries[0].isIntersecting;
        if (running) requestAnimationFrame(tick);
      }, { threshold: 0 }).observe(host);
    }

    function resize() {
      var w = host.clientWidth, h = host.clientHeight;
      if (!w || !h) return;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    }
    window.addEventListener('resize', resize);

    var t = 0;
    function tick() {
      if (!running) return;
      t += 0.008;
      tub.rotation.y += 0.0045;
      tub.position.y = Math.sin(t * 1.4) * 0.14;
      // parallax: scene leans toward the pointer
      scene.rotation.y += ((mouseX * 0.35) - scene.rotation.y) * 0.04;
      scene.rotation.x += ((mouseY * 0.18) - scene.rotation.x) * 0.04;
      voltDust.rotation.y = t * 0.05;
      boltDust.rotation.y = -t * 0.04;
      voltDust.position.y = Math.sin(t * 0.7) * 0.25;
      boltDust.position.y = Math.cos(t * 0.6) * 0.25;
      renderer.render(scene, camera);
      requestAnimationFrame(tick);
    }
    resize();
    tick();
  }
})();
